import { IRemisionDetail } from "src/types/global";
import { getCleanCurrency } from "@utils/utils";
import { formatSpanishLongDate, formatSpanishShortDate } from "@utils/dateFormats";
import { formatQuantity } from "@utils/quantityFormat";

/**
 * The remision print document's single formatting boundary. Every field is
 * already a formatted `string` so the PDF component (RemisionPDFContent)
 * performs zero formatting and imports no formatter — a `number` cannot
 * reach the component.
 */
export interface IRemisionPrintItem {
  description: string;
  quantity: string;
  unit: string; // always "" — no unit-of-measure data source (decision #4)
  unitPrice: string;
  iva: string; // always "" — no per-line IVA data source (decision #4)
  lineTotal: string;
}

export interface IRemisionPrintModel {
  remisionNumber: string;
  clientName: string;
  clientIdentification: string;
  clientAddress: string;
  clientCity: string; // always "" — no City column on Client
  clientPhone: string;
  concept: string; // always "" — no data source (decision #4)
  documentDate: string;
  dueDate: string;
  createdBy: string;
  paymentMethod: string;
  amountInWords: string; // always "" — no number-to-words helper (decision #5)
  items: IRemisionPrintItem[];
  subtotal: string;
  total: string;
}

/**
 * Builds the print view model from a freshly fetched, raw `IRemisionDetail`
 * (numeric price/total, ISO dates) — never from the detail page's local
 * state, which is destructively pre-formatted (RemisionDetail.tsx:79-80/:104).
 */
export const buildRemisionPrintModel = (dto: IRemisionDetail): IRemisionPrintModel => {
  const totalFormatted = getCleanCurrency(dto.total);

  return {
    remisionNumber: String(dto.id),
    clientName: dto.clientName,
    clientIdentification: dto.clientIdentification ?? "",
    clientAddress: dto.clientAddress ?? "",
    clientCity: "",
    clientPhone: dto.clientPhone ?? "",
    concept: "",
    documentDate: formatSpanishLongDate(dto.creationTime),
    dueDate: formatSpanishShortDate(dto.limitDate),
    createdBy: dto.createdByEmployeeName ?? "",
    // Branch on the payment OBJECT, never on the number — paymentMethod 0 (cash) is falsy.
    paymentMethod: dto.payment
      ? dto.payment.paymentMethod === 1
        ? "Transferencia"
        : "Contado"
      : "",
    // WARNING-L follow-up (verify-report obs #61 rev 6, formerly
    // SUGGESTION-N): if a future change ever populates this field with a
    // real Spanish number-to-words string (decision #5 is what keeps it
    // "" today), re-measure `FOOTER_RESERVED_HEIGHT` in
    // RemisionPDFContent.tsx before shipping — the footer's structural
    // margin over its natural content height is only 0.77pt (verified
    // 2026-08-07), and `amountInWordsValue`'s `minHeight: 10` does not cap
    // how far real multi-line text can grow it. 3 wrapped lines (~188
    // chars) measured 47.38pt of natural footer height against a 41pt
    // reservation.
    amountInWords: "",
    items: dto.requestProducts.map((product) => ({
      description: product.productName,
      // Quantity is a QUANTITY, not money — getCleanCurrency (COP, 0
      // fraction digits) would silently round a fractional quantity like 2.5
      // to 3. See formatQuantity / CRITICAL-1.
      quantity: formatQuantity(product.quantity),
      unit: "",
      unitPrice: getCleanCurrency(product.price),
      iva: "",
      lineTotal: getCleanCurrency(product.total),
    })),
    subtotal: totalFormatted,
    total: totalFormatted,
  };
};
